import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronLeft, X, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import OrgAutocomplete from '../OrgAutocomplete';
import MonthYearPicker from '../MonthYearPicker';
import SchoolYearPicker from '../SchoolYearPicker';
import DateSenseChecks from '../DateSenseChecks';
import { CarerNamesInput } from '../CarerNamesInput';
import { RespiteCarerPicker } from '../RespiteCarerPicker';
import { organisationsApi } from '../../lib/api';
import { normaliseName } from '../../lib/textCase';
import {
  WIZARD_TILES,
  FOSTER_HOME_TILE,
  getOrgType,
  ORG_TYPES_WITH_PLACEMENTS,
  PLACING_ORG_TYPES,
  PLACED_HOME_ORG_TYPES,
  HOLDING_ORG_NAME,
  PLACEMENT_TYPES,
  CARER_LED_TYPES,
  BUILDING_LED_TYPES,
  rolesFor,
  roleLabel,
  formatMonthYear,
  respitePatternLabel,
} from '../../lib/orgTypes';

/**
 * Add an organisation — one question per step, inside a fixed-size dialog
 * over the Organisations list.
 *
 * Steps: what kind → which one → roughly when → who you remember →
 * (councils and fostering agencies only) where they placed you →
 * anything else → check and save. Every step after the first has a plain
 * way past it; only the type and a name are needed to save.
 *
 * The draft lives in sessionStorage so closing the tab or the dialog
 * part-way through loses nothing, and closing with unsaved answers asks
 * first ("Keep what you've entered?"). Saves through the same
 * POST /organisations as the edit dialog — no data-model change.
 *
 * Placement-first: the first tile is "Foster home". It isn't an
 * organisation — nobody holds a file at a foster home — so that path asks
 * who looked after you, roughly when, and then *which council or agency
 * placed you there*: one of your existing ones, one you name now, or "I
 * don't know yet" (filed under a "Council not yet known" holder the app
 * resolves later). A children's home gets the same "who placed you?" step,
 * because it is both a place you lived and an organisation with records.
 *
 * Props:
 *  - open, onOpenChange
 *  - dob — user's date of birth, for the date sense-checks and school years
 *  - organisations — the user's current organisations (to pick a placing council)
 *  - respitePool / onRespitePoolChange — the user's short-break carers and a
 *    persister for newly named ones (PUT /profile), so the foster-home path
 *    can record who gave respite
 *  - onAdded(result) — after a successful save. `result.kind` is
 *    "organisation" (a new org, maybe with child/placing orgs),
 *    "placement" (added under an existing org) or "holding" (filed under
 *    "Council not yet known"); see `save()` for the fields.
 *  - initialType — jump straight past the tile grid to this type's
 *    questions, as if it had just been picked (used by dashboard nudges
 *    like "do you remember your secondary school?" that already know what
 *    they're suggesting). Ignored once a draft is resumed, and only
 *    applied on the transition from closed to open.
 */
const DRAFT_KEY = 'pd.addOrganisationDraft';

const EMPTY = {
  type: '',
  name: '',
  location: '',
  address: '',
  email: '',
  website: '',
  start_date: '',
  end_date: '',
  dates_unknown: false,
  year_group_start: '',
  year_group_end: '',
  professionals: [],
  placements: [],
  reference_numbers: '',
  notes: '',
  canonical_org_id: null,
  contact_from_match: false,
  // Set when the org was found by searching a name it's no longer known by
  // for this purpose — a dissolved trust, an abolished predecessor council,
  // or a still-operating council that has delegated this function elsewhere
  // — see OrgAutocomplete's matched_alias. Carried through to the SAR
  // letter so it can note the old name for the receiving organisation's
  // benefit.
  matched_alias: null,
  // Foster-home path: the carers (the placement's people) and short breaks
  carer_names: [],
  respite: [],
  // "Who placed you there?" — mode: 'existing' | 'new' | 'unknown'
  placed_by: { mode: '', org_id: null, new_type: 'council', new_name: '', new_canonical: null, new_contact: null },
};

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `p_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const readDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data?.type ? parsed : null;
  } catch {
    return null;
  }
};
const writeDraft = (data, stepIndex) => {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ data, stepIndex })); } catch { /* ignore */ }
};
const clearDraft = () => {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
};

const stepsFor = (type) => {
  if (type === FOSTER_HOME_TILE.value) return ['type', 'home', 'when', 'respite', 'placed_by', 'review'];
  return [
    'type',
    'name',
    'when',
    'people',
    ...(ORG_TYPES_WITH_PLACEMENTS.includes(type) ? ['placements'] : []),
    ...(PLACED_HOME_ORG_TYPES.includes(type) ? ['placed_by'] : []),
    'extras',
    'review',
  ];
};

const isDirty = (data) =>
  !!data.type && (
    data.name || data.location || data.start_date || data.end_date
    || data.professionals.length || data.placements.length || (data.carer_names || []).length
    || data.reference_numbers || data.notes || data.placed_by?.mode
  );

const placedByReady = (pb) =>
  pb?.mode === 'existing' ? !!pb.org_id
    : pb?.mode === 'new' ? !!(pb.new_name || '').trim()
      : pb?.mode === 'unknown';

export const AddOrganisationWizard = ({ open, onOpenChange, dob, organisations = [], respitePool = [], onRespitePoolChange, onAdded, initialType = null }) => {
  const [data, setData] = useState(EMPTY);
  const [stepIndex, setStepIndex] = useState(0);
  const [resumed, setResumed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guardOpen, setGuardOpen] = useState(false);
  const headingRef = useRef(null);

  const steps = useMemo(() => stepsFor(data.type), [data.type]);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const typeInfo = getOrgType(data.type);
  const singular = typeInfo?.singular || 'organisation';
  const isFosterHome = data.type === FOSTER_HOME_TILE.value;
  // Short-break carers named in this wizard are kept here as well as being
  // handed to the parent to persist, so their row shows at once rather than
  // after the profile round-trip.
  const [localCarers, setLocalCarers] = useState([]);
  const mergedRespitePool = useMemo(() => {
    const ids = new Set(respitePool.map((c) => c.id));
    return [...respitePool, ...localCarers.filter((c) => !ids.has(c.id))];
  }, [respitePool, localCarers]);
  // Includes unresolved "Council not yet known" holders deliberately — the
  // only other route to one is an exact-text match on the freeform town
  // field in the "I don't know yet" option below, which silently creates a
  // duplicate holder the moment the user types the town even slightly
  // differently from last time. Picking one here just sets placed_by_org_id
  // like any other organisation; no special-casing needed downstream.
  const placingOrgs = useMemo(
    () => organisations.filter((o) => PLACING_ORG_TYPES.includes(o.type)),
    [organisations]
  );

  // Open: pick the draft back up if there is one, else start fresh.
  useEffect(() => {
    if (!open) return;
    const draft = readDraft();
    if (draft) {
      setData({ ...EMPTY, ...draft.data });
      setStepIndex(Math.min(draft.stepIndex || 0, stepsFor(draft.data.type).length - 1));
      setResumed(true);
    } else if (initialType && WIZARD_TILES.some((t) => t.value === initialType)) {
      // Same effect as picking the tile by hand: skip straight to step 1.
      setData({ ...EMPTY, type: initialType });
      setStepIndex(1);
      setResumed(false);
    } else {
      setData(EMPTY);
      setStepIndex(0);
      setResumed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the draft current while the dialog is open.
  useEffect(() => {
    if (!open) return;
    if (isDirty(data)) writeDraft(data, stepIndex);
  }, [open, data, stepIndex]);

  // Move focus to the question on each step so it is announced and the
  // dialog never leaves focus on a button that just disappeared — unless a
  // field on the new step has already taken focus (the name step
  // auto-focuses its input), in which case stealing it would swallow the
  // first thing the user types.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const active = document.activeElement;
      const typing = active && ['INPUT', 'TEXTAREA'].includes(active.tagName);
      if (!typing) headingRef.current?.focus();
    }, 30);
    return () => clearTimeout(t);
  }, [open, step]);

  const update = useCallback((patch) => setData((prev) => ({ ...prev, ...patch })), []);

  const goTo = (name) => setStepIndex(Math.max(0, steps.indexOf(name)));
  const next = () => setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setStepIndex((i) => Math.max(i - 1, 0));

  const startAgain = () => {
    clearDraft();
    setLocalCarers([]);
    setData(EMPTY);
    setStepIndex(0);
    setResumed(false);
  };

  // Closing: straight out if nothing has been entered, otherwise ask.
  const requestClose = () => {
    if (isDirty(data)) {
      setGuardOpen(true);
      return;
    }
    clearDraft();
    onOpenChange(false);
  };
  const keepDraftAndClose = () => {
    writeDraft(data, stepIndex);
    setGuardOpen(false);
    onOpenChange(false);
  };
  const discardAndClose = () => {
    clearDraft();
    setGuardOpen(false);
    setData(EMPTY);
    setStepIndex(0);
    onOpenChange(false);
  };

  const chooseType = (value) => {
    setData((prev) => ({
      ...prev,
      type: value,
      // Roles are per type; placements only exist for some types.
      professionals: prev.type === value ? prev.professionals : [],
      placements: ORG_TYPES_WITH_PLACEMENTS.includes(value) ? prev.placements : [],
      year_group_start: value === 'school' ? prev.year_group_start : '',
      year_group_end: value === 'school' ? prev.year_group_end : '',
    }));
    // Picking a tile is the answer — move straight on.
    setStepIndex(1);
  };

  const buildFosterPlacement = () => ({
    type: 'foster_placement',
    name: data.name.trim(),
    carer_names: data.carer_names || [],
    respite: (data.respite || []).map((e) => ({ carer_id: e.carer_id, pattern: e.pattern || null })),
    address: '',
    start_date: data.dates_unknown ? '' : (data.start_date || ''),
    end_date: data.dates_unknown ? '' : (data.end_date || ''),
  });

  const save = async () => {
    const pb = data.placed_by || {};
    if (isFosterHome) {
      if (!(data.carer_names || []).length && !data.name.trim()) {
        goTo('home');
        toast.error('Add a carer\u2019s name, or what you called the placement.');
        return;
      }
      if (!placedByReady(pb)) {
        goTo('placed_by');
        toast.error('Say who placed you there, or choose \u201cI don\u2019t know yet\u201d.');
        return;
      }
    } else if (!data.type || !data.name.trim()) {
      goTo('name');
      toast.error(`Give the ${singular} a name first.`);
      return;
    }
    setSaving(true);
    try {
      let result;
      if (isFosterHome) {
        const placement = buildFosterPlacement();
        if (pb.mode === 'existing') {
          const target = placingOrgs.find((o) => o.id === pb.org_id);
          const res = await organisationsApi.addPlacement(pb.org_id, placement);
          result = { kind: 'placement', organisation: target, placement: res.data?.placement, childOrganisations: res.data?.child_organisation ? [res.data.child_organisation] : [], addressesAdded: [] };
        } else if (pb.mode === 'new') {
          const res = await organisationsApi.add({
            type: pb.new_type || 'council',
            name: pb.new_name.trim(),
            location: data.location || '',
            canonical_org_id: pb.new_canonical || null,
            ...(pb.new_contact || {}),
            placements: [placement],
          });
          result = { kind: 'organisation', organisation: res.data?.organisation, addressesAdded: res.data?.addresses_auto_added || [], childOrganisations: [], placingOrganisation: null };
        } else {
          const res = await organisationsApi.add({
            type: 'council',
            unresolved: true,
            name: HOLDING_ORG_NAME,
            location: data.location || '',
            placements: [placement],
          });
          result = { kind: 'holding', organisation: res.data?.organisation, holdingReused: !!res.data?.holding_org_reused, addressesAdded: [], childOrganisations: [] };
        }
      } else {
        const payload = {
          type: data.type,
          name: data.name.trim(),
          location: data.location || '',
          address: data.address || '',
          email: data.email || '',
          website: data.website || '',
          start_date: data.dates_unknown ? '' : (data.start_date || ''),
          end_date: data.dates_unknown ? '' : (data.end_date || ''),
          year_group_start: data.year_group_start || '',
          year_group_end: data.year_group_end || '',
          placements: data.placements,
          social_workers: data.professionals,
          notes: data.notes || '',
          reference_numbers: data.reference_numbers || '',
          canonical_org_id: data.canonical_org_id || null,
          matched_alias: data.matched_alias || null,
        };
        if (steps.includes('placed_by') && pb.mode) {
          if (pb.mode === 'existing' && pb.org_id) payload.placed_by_org_id = pb.org_id;
          else if (pb.mode === 'new' && pb.new_name.trim()) {
            payload.placed_by_new_org = { type: pb.new_type || 'council', name: pb.new_name.trim(), canonical_org_id: pb.new_canonical || null, ...(pb.new_contact || {}) };
            payload.placed_by_location = data.location || '';
          } else if (pb.mode === 'unknown') {
            payload.placed_by_unknown = true;
            payload.placed_by_location = data.location || '';
          }
        }
        const res = await organisationsApi.add(payload);
        result = {
          kind: 'organisation',
          organisation: res.data?.organisation,
          addressesAdded: res.data?.addresses_auto_added || [],
          childOrganisations: res.data?.child_organisations || [],
          placingOrganisation: res.data?.placing_organisation || null,
        };
      }
      clearDraft();
      onOpenChange(false);
      onAdded?.(result);
      setData(EMPTY);
      setStepIndex(0);
    } catch {
      toast.error('It could not be saved. Your answers are still here \u2014 try again.');
    } finally {
      setSaving(false);
    }
  };

  const stepNumber = stepIndex + 1;
  const total = steps.length;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); }}>
        <DialogContent
          className="p-0 gap-0 flex flex-col overflow-hidden sm:max-w-[800px] w-[96vw] h-[min(660px,92dvh)] max-sm:w-full max-sm:h-[96dvh] max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-t-xl max-sm:rounded-b-none [&>button.absolute]:hidden"
          onInteractOutside={(e) => { if (isDirty(data)) e.preventDefault(); }}
          data-testid="add-org-wizard"
        >
          {/* Header: where you are, and the one way out */}
          <div className="flex items-center justify-between gap-3 px-5 sm:px-8 pt-5 sm:pt-6">
            <p className="pd-eyebrow" data-testid="add-org-wizard-step">
              Add an organisation · step {stepNumber} of {total}
            </p>
            <div className="flex items-center gap-3 sm:gap-4">
              <StepDots total={total} current={stepIndex} />
              <Button variant="ghost" size="icon" onClick={requestClose} aria-label="Close" data-testid="add-org-wizard-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Body: the question */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-8 pt-4 pb-6 space-y-6">
            {resumed && stepIndex > 0 && (
              <p className="text-sm text-ink-55" data-testid="add-org-wizard-resumed">
                Carrying on where you left off.{' '}
                <button type="button" className="font-semibold text-brand hover:text-brand-ink" onClick={startAgain}>
                  Start again
                </button>
              </p>
            )}

            {step === 'type' && (
              <StepFrame
                headingRef={headingRef}
                title="What kind of place was it?"
                sub="Pick one. Start with the home if that is what you remember best \u2014 the council can come later."
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3" role="list">
                  {WIZARD_TILES.map((t) => {
                    const Icon = t.icon;
                    const selected = data.type === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        role="listitem"
                        onClick={() => chooseType(t.value)}
                        aria-pressed={selected}
                        className={`text-left rounded-lg p-4 sm:p-5 flex flex-col gap-2.5 transition-colors duration-state border-2 ${
                          selected ? 'border-brand bg-surface' : 'border-transparent bg-ground hover:bg-inset'
                        }`}
                        data-testid={`add-org-type-${t.value}`}
                      >
                        <Icon className="w-6 h-6 text-ink stroke-[1.75]" aria-hidden="true" />
                        <span className="font-heading text-[17px] sm:text-[19px] font-semibold tracking-[-0.02em] text-ink leading-tight">{t.label}</span>
                        <span className="text-sm text-ink-55 hidden sm:block">{t.description}</span>
                      </button>
                    );
                  })}
                </div>
              </StepFrame>
            )}

            {step === 'name' && (
              <StepFrame
                headingRef={headingRef}
                title={data.type === 'council' ? 'Which council was it?' : `Which ${singular} was it?`}
                sub="Start typing. We match it to the right records team, so you do not need their email or address."
              >
                <div className="space-y-2 max-w-xl">
                  <Label className="text-[15px] font-semibold" htmlFor="add-org-name">
                    {typeInfo?.label || 'Organisation'} name
                  </Label>
                  <OrgAutocomplete
                    value={data.name}
                    type={data.type}
                    autoFocus
                    onChange={(v) => update({ name: v, canonical_org_id: null, contact_from_match: false, matched_alias: null })}
                    onBlur={(e) => update({ name: normaliseName(e.target.value) || e.target.value })}
                    onPick={(picked) => update({
                      name: picked.name,
                      email: picked.privacy_email || '',
                      address: picked.address || '',
                      website: picked.website || '',
                      canonical_org_id: picked.id,
                      contact_from_match: !!(picked.privacy_email || picked.address || picked.website),
                      matched_alias: picked.matched_alias || null,
                    })}
                    placeholder={data.type === 'council' ? 'For example, Birmingham City Council' : `For example, the ${singular} you went to`}
                    testId="add-org-name"
                  />
                  {data.contact_from_match && (
                    <p className="text-sm text-brand-ink" data-testid="add-org-contact-found">
                      Found their records team. You will not need to look up their details.
                    </p>
                  )}
                  {data.matched_alias && (
                    <p className="text-sm text-brand-ink" data-testid="add-org-matched-alias">
                      &ldquo;{data.matched_alias}&rdquo; no longer handles this directly &mdash;{' '}
                      {data.name} does, including (very likely) records from before any handover.
                      We will mention the old name in your Subject Access Request so they can
                      trace them.
                    </p>
                  )}
                </div>
                <div className="space-y-2 max-w-xl">
                  <Label className="text-[15px] font-semibold" htmlFor="add-org-town">Which town or city?</Label>
                  <p className="text-sm text-ink-55">Helps tell two places with the same name apart.</p>
                  <Input
                    id="add-org-town"
                    value={data.location}
                    onChange={(e) => update({ location: e.target.value })}
                    onBlur={(e) => update({ location: normaliseName(e.target.value) || e.target.value })}
                    placeholder="For example, Birmingham"
                    data-testid="add-org-town"
                  />
                </div>
                <div className="rounded-lg bg-ground p-4 max-w-xl space-y-1">
                  <p className="font-semibold text-ink">I don&apos;t remember the name</p>
                  <p className="text-sm text-ink-55">
                    That is fine. Put what you do remember in the name box — &ldquo;the primary school in Erdington&rdquo;,
                    &ldquo;the GP near Nan&apos;s&rdquo; — and the town below. You can change it once you know more.
                  </p>
                </div>
              </StepFrame>
            )}

            {step === 'home' && (
              <StepFrame
                headingRef={headingRef}
                title="Who looked after you there?"
                sub="Foster carers\u2019 names, or a family name \u2014 whatever you knew them by. First names are fine."
              >
                <div className="space-y-2 max-w-xl">
                  <Label className="text-[15px] font-semibold">Carers</Label>
                  <CarerNamesInput
                    value={data.carer_names || []}
                    onChange={(arr) => update({ carer_names: arr.map((n) => normaliseName(n) || n) })}
                    placeholder="Type a name and press Enter"
                    testId="add-org-home-carers"
                  />
                </div>
                <div className="space-y-2 max-w-xl">
                  <Label className="text-[15px] font-semibold" htmlFor="add-org-home-name">What did you call the placement?</Label>
                  <p className="text-sm text-ink-55">Optional \u2014 for example \u201cThe Okoros\u201d or \u201cNan\u2019s\u201d.</p>
                  <Input
                    id="add-org-home-name"
                    value={data.name}
                    onChange={(e) => update({ name: e.target.value })}
                    onBlur={(e) => update({ name: normaliseName(e.target.value) || e.target.value })}
                    data-testid="add-org-home-name"
                  />
                </div>
                <div className="space-y-2 max-w-xl">
                  <Label className="text-[15px] font-semibold" htmlFor="add-org-home-town">Which town or area?</Label>
                  <p className="text-sm text-ink-55">Helps work out which council it was, if you are not sure.</p>
                  <Input
                    id="add-org-home-town"
                    value={data.location}
                    onChange={(e) => update({ location: e.target.value })}
                    onBlur={(e) => update({ location: normaliseName(e.target.value) || e.target.value })}
                    placeholder="For example, Erdington"
                    data-testid="add-org-town"
                  />
                </div>
              </StepFrame>
            )}

            {step === 'respite' && (
              <StepFrame
                headingRef={headingRef}
                title="Did anyone else look after you for short breaks?"
                sub="Weekends, school holidays, or while your carers were away. Councils keep records of these too, and they are easy to miss. Skip it if there was nobody."
              >
                <div className="max-w-2xl">
                  <RespiteCarerPicker
                    attached={data.respite || []}
                    pool={mergedRespitePool}
                    hint={null}
                    onChange={({ attached, pool }) => {
                      update({ respite: attached });
                      if (pool.length !== mergedRespitePool.length) {
                        setLocalCarers(pool.filter((c) => !respitePool.some((r) => r.id === c.id)));
                        onRespitePoolChange?.(pool);
                      }
                    }}
                    testId="add-org-respite"
                  />
                </div>
              </StepFrame>
            )}

            {step === 'placed_by' && (
              <PlacedByStep
                headingRef={headingRef}
                data={data}
                update={update}
                placingOrgs={placingOrgs}
                isFosterHome={isFosterHome}
              />
            )}

            {step === 'when' && (
              <StepFrame
                headingRef={headingRef}
                title={isFosterHome ? 'Roughly when did you live there?' : `Roughly when were you with ${data.name || `this ${singular}`}?`}
                sub="A month and year is plenty. Guessing is fine — the records will show the real dates."
              >
                {data.type === 'school' && (
                  <SchoolYearPicker
                    dob={dob}
                    yearGroupStart={data.year_group_start}
                    yearGroupEnd={data.year_group_end}
                    startDate={data.start_date}
                    endDate={data.end_date}
                    testIdPrefix="add-org-school-yp"
                    onChange={(patch) => update({ ...patch, dates_unknown: false })}
                  />
                )}
                <div className={`grid sm:grid-cols-2 gap-4 max-w-xl ${data.dates_unknown ? 'opacity-50' : ''}`}>
                  <div className="space-y-2">
                    <Label className="text-[15px] font-semibold">From</Label>
                    <MonthYearPicker
                      value={data.start_date}
                      disabled={data.dates_unknown}
                      onChange={(v) => update({ start_date: v })}
                      testId="add-org-start"
                      ariaLabel="Start date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[15px] font-semibold">Until</Label>
                    <p className="text-sm text-ink-55 -mt-1">Leave blank if it is still going.</p>
                    <MonthYearPicker
                      value={data.end_date}
                      disabled={data.dates_unknown}
                      onChange={(v) => update({ end_date: v })}
                      testId="add-org-end"
                      ariaLabel="End date"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-3 text-[15px] text-ink-70 cursor-pointer max-w-xl">
                  <Checkbox
                    checked={data.dates_unknown}
                    onCheckedChange={(v) => update({ dates_unknown: !!v, ...(v ? { start_date: '', end_date: '' } : {}) })}
                    data-testid="add-org-dates-unknown"
                  />
                  I don&apos;t know the dates
                </label>
                {!data.dates_unknown && (
                  <DateSenseChecks dob={dob} startDate={data.start_date} endDate={data.end_date} type={data.type} testIdPrefix="add-org-date-check" />
                )}
              </StepFrame>
            )}

            {step === 'people' && (
              <PeopleStep
                headingRef={headingRef}
                data={data}
                update={update}
                singular={singular}
              />
            )}

            {step === 'placements' && (
              <PlacementsStep headingRef={headingRef} data={data} update={update} />
            )}

            {step === 'extras' && (
              <ExtrasStep headingRef={headingRef} data={data} update={update} />
            )}

            {step === 'review' && (
              <ReviewStep headingRef={headingRef} data={data} steps={steps} goTo={goTo} singular={singular} placingOrgs={placingOrgs} isFosterHome={isFosterHome} respitePool={mergedRespitePool} />
            )}
          </div>

          {/* Footer: back, the way past this step, and forward */}
          <div className="flex items-center justify-between gap-2 px-5 sm:px-8 py-4 border-t border-rule">
            {stepIndex === 0 ? (
              <Button variant="ghost" onClick={requestClose}>Cancel</Button>
            ) : (
              <Button variant="ghost" onClick={back} data-testid="add-org-back">
                <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" /> Back
              </Button>
            )}
            <div className="flex items-center gap-2">
              {step === 'people' && (
                <Button variant="secondary" onClick={next} data-testid="add-org-skip-people">
                  I don&apos;t remember anyone
                </Button>
              )}
              {step === 'placements' && (
                <Button variant="secondary" onClick={next} data-testid="add-org-skip-placements">
                  {data.placements.length ? 'That is all of them' : 'They did not place me'}
                </Button>
              )}
              {step === 'extras' && (
                <Button variant="secondary" onClick={next} data-testid="add-org-skip-extras">Skip</Button>
              )}
              {step === 'name' && (
                <Button onClick={next} disabled={!data.name.trim()} data-testid="add-org-continue">Continue</Button>
              )}
              {step === 'home' && (
                <Button onClick={next} disabled={!(data.carer_names || []).length && !data.name.trim()} data-testid="add-org-continue">Continue</Button>
              )}
              {step === 'placed_by' && (
                <Button onClick={next} disabled={!placedByReady(data.placed_by)} data-testid="add-org-continue">Continue</Button>
              )}
              {step === 'respite' && (
                <>
                  <Button variant="secondary" onClick={() => { update({ respite: [] }); next(); }} data-testid="add-org-skip-respite">There was nobody</Button>
                  <Button onClick={next} data-testid="add-org-continue">Continue</Button>
                </>
              )}
              {(step === 'when') && (
                <Button onClick={next} data-testid="add-org-continue">Continue</Button>
              )}
              {(step === 'people' || step === 'placements' || step === 'extras') && (
                <Button onClick={next} data-testid="add-org-continue">Continue</Button>
              )}
              {step === 'review' && (
                <Button onClick={save} disabled={saving} data-testid="add-org-save">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />}
                  {isFosterHome ? 'Add this foster home' : `Add ${data.name.trim() || `this ${singular}`}`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Closing part-way through */}
      <AlertDialog open={guardOpen} onOpenChange={setGuardOpen}>
        <AlertDialogContent data-testid="add-org-wizard-guard">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              Keep what you&apos;ve entered?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ink-70">
              You are part-way through adding {data.name.trim() || `a ${singular}`}. We can keep it as a draft so you can
              carry on next time you press &ldquo;Add an organisation&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <Button variant="destructive" onClick={discardAndClose} data-testid="add-org-wizard-discard">Discard</Button>
            <AlertDialogAction onClick={keepDraftAndClose} data-testid="add-org-wizard-keep">Keep the draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
const StepDots = ({ total, current }) => (
  <div className="hidden sm:flex items-center gap-2" aria-hidden="true">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={`w-2 h-2 rounded-full ${i === current ? 'bg-brand' : i < current ? 'bg-shape' : 'bg-rule'}`}
      />
    ))}
  </div>
);

/** Heading is the DialogTitle so Radix and screen readers agree on what this step is. */
const StepFrame = ({ headingRef, title, sub, children }) => (
  <div className="space-y-6">
    <div className="space-y-2">
      <DialogTitle
        ref={headingRef}
        tabIndex={-1}
        className="font-heading text-[26px] sm:text-[30px] font-bold tracking-[-0.035em] leading-[1.1] text-ink outline-none"
      >
        {title}
      </DialogTitle>
      {sub && <DialogDescription className="text-base text-ink-70">{sub}</DialogDescription>}
    </div>
    {children}
  </div>
);

const Chip = ({ children, onRemove, removeLabel }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-brand-wash text-brand-ink px-3 py-1.5 text-sm font-medium">
    {children}
    {onRemove && (
      <button type="button" onClick={onRemove} aria-label={removeLabel} className="rounded-full hover:bg-surface/60 p-0.5">
        <X className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
    )}
  </span>
);

// ---- Who do you remember? ------------------------------------------------
const PeopleStep = ({ headingRef, data, update, singular }) => {
  const roles = rolesFor(data.type);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const nameRef = useRef(null);

  const add = () => {
    const clean = normaliseName(name.trim()) || name.trim();
    if (!clean) return;
    update({ professionals: [...data.professionals, { name: clean, role: role || roles[0]?.value || 'other', dates_known: '' }] });
    setName('');
    setRole('');
    nameRef.current?.focus();
  };
  const remove = (i) => update({ professionals: data.professionals.filter((_, idx) => idx !== i) });

  return (
    <StepFrame
      headingRef={headingRef}
      title={`Do you remember anyone from ${data.name || `this ${singular}`}?`}
      sub="Names help them find your records. First names are fine. If nothing comes to mind, that is normal — skip it."
    >
      {data.professionals.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="add-org-people-list">
          {data.professionals.map((p, i) => (
            <Chip key={`${p.name}-${i}`} onRemove={() => remove(i)} removeLabel={`Remove ${p.name}`}>
              {p.name} · {roleLabel(data.type, p.role)}
            </Chip>
          ))}
        </div>
      )}
      <div className="space-y-2 max-w-2xl">
        <Label className="text-[15px] font-semibold" htmlFor="add-org-person-name">Add someone</Label>
        <p className="text-sm text-ink-55">Type a name, then pick what they did if you know it.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            id="add-org-person-name"
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Name, or just a first name"
            className="flex-1"
            data-testid="add-org-person-name"
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="sm:w-[240px]" aria-label="What did they do?" data-testid="add-org-person-role">
              <SelectValue placeholder="What did they do?" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={add} disabled={!name.trim()} data-testid="add-org-person-add">Add</Button>
        </div>
      </div>
      <div className="space-y-2 max-w-2xl">
        <p className="text-sm text-ink-55">Roles people often had at a {singular} — tap one, then add their name:</p>
        <div className="flex flex-wrap gap-2">
          {roles.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => { setRole(r.value); nameRef.current?.focus(); }}
              aria-pressed={role === r.value}
              className={`rounded-full px-3.5 py-2 text-sm font-medium border transition-colors duration-state ${
                role === r.value ? 'bg-brand-wash text-brand-ink border-brand-wash' : 'bg-ground text-ink-70 border-rule hover:bg-inset'
              }`}
              data-testid={`add-org-role-pill-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
    </StepFrame>
  );
};

// ---- Where did they place you? -------------------------------------------
const emptyPlacement = () => ({ id: newId(), type: '', name: '', address: '', carer_names: [], start_date: '', end_date: '', notes: '', promote_to_org: true });

const PlacementsStep = ({ headingRef, data, update }) => {
  const [editing, setEditing] = useState(null); // placement being added/edited, or null
  const isCarerLed = CARER_LED_TYPES.has(editing?.type);
  const isBuildingLed = BUILDING_LED_TYPES.has(editing?.type);

  const savePlacement = () => {
    if (!editing?.type) return;
    const clean = { ...editing, name: normaliseName(editing.name.trim()) || editing.name.trim() };
    const exists = data.placements.some((p) => p.id === clean.id);
    update({ placements: exists ? data.placements.map((p) => (p.id === clean.id ? clean : p)) : [...data.placements, clean] });
    setEditing(null);
  };
  const remove = (id) => update({ placements: data.placements.filter((p) => p.id !== id) });

  return (
    <StepFrame
      headingRef={headingRef}
      title="Where did they place you?"
      sub="Foster homes, children's homes and similar — one at a time. Whichever you remember: the carers' names or the address."
    >
      {data.placements.length > 0 && !editing && (
        <div className="space-y-2" data-testid="add-org-placements-list">
          {data.placements.map((p) => (
            <div key={p.id} className="rounded-lg bg-ground p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="pd-mono">{PLACEMENT_TYPES.find((t) => t.value === p.type)?.label || 'Placement'}</p>
                <p className="font-heading text-[17px] font-semibold tracking-[-0.02em] text-ink mt-1 truncate">
                  {p.name || (p.carer_names?.length ? p.carer_names.join(', ') : 'Unnamed')}
                </p>
                <p className="text-sm text-ink-55">
                  {[formatMonthYear(p.start_date), formatMonthYear(p.end_date)].filter(Boolean).join(' – ') || 'Dates not known'}
                  {p.carer_names?.length && p.name ? ` · ${p.carer_names.join(', ')}` : ''}
                  {BUILDING_LED_TYPES.has(p.type) && p.promote_to_org !== false ? ' · also its own organisation' : ''}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>Change</Button>
                <Button variant="ghost" size="icon" onClick={() => remove(p.id)} aria-label={`Remove ${p.name || 'this placement'}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!editing ? (
        <Button variant="secondary" onClick={() => setEditing(emptyPlacement())} data-testid="add-org-add-placement">
          <Plus className="w-4 h-4 mr-1.5" aria-hidden="true" />
          {data.placements.length ? 'Add another home' : 'Add a home'}
        </Button>
      ) : (
        <div className="rounded-lg bg-ground p-4 sm:p-5 space-y-5" data-testid="add-org-placement-form">
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold">What kind of home was it?</Label>
            <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v, promote_to_org: BUILDING_LED_TYPES.has(v) })}>
              <SelectTrigger className="bg-surface border-surface" aria-label="Placement type" data-testid="add-org-placement-type">
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent>
                {PLACEMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {editing.type && (
            <>
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">
                  {isCarerLed ? 'What did you call it?' : 'What was it called?'}
                </Label>
                <p className="text-sm text-ink-55">
                  {isCarerLed ? 'A name you know them by, for example The Smiths. Blank is fine if you add carers below.' : 'The name of the home or unit.'}
                </p>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="bg-surface border-surface"
                  aria-label="Placement name"
                  data-testid="add-org-placement-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">
                  {isCarerLed ? 'Who looked after you there?' : 'Who worked with you there?'}
                </Label>
                <p className="text-sm text-ink-55">{isCarerLed ? 'Foster carers, kinship carers or hosts.' : 'Key workers, residential staff or carers.'}</p>
                <CarerNamesInput
                  value={editing.carer_names || []}
                  onChange={(arr) => setEditing({ ...editing, carer_names: arr })}
                  placeholder="Type a name and press Enter"
                  testId="add-org-placement-carers"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[15px] font-semibold">When did you move in?</Label>
                  <MonthYearPicker value={editing.start_date} onChange={(v) => setEditing({ ...editing, start_date: v })} ariaLabel="Move-in date" className="[&_button]:bg-surface [&_button]:border-surface" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[15px] font-semibold">When did you leave?</Label>
                  <MonthYearPicker value={editing.end_date} onChange={(v) => setEditing({ ...editing, end_date: v })} ariaLabel="Move-out date" className="[&_button]:bg-surface [&_button]:border-surface" />
                </div>
              </div>
              {isBuildingLed && (
                <label className="flex items-start gap-3 rounded-md bg-inset p-3 cursor-pointer">
                  <Checkbox
                    className="mt-0.5"
                    checked={editing.promote_to_org !== false}
                    onCheckedChange={(v) => setEditing({ ...editing, promote_to_org: !!v })}
                    data-testid="add-org-placement-promote"
                  />
                  <span className="text-sm text-ink-70 leading-relaxed">
                    <span className="block font-semibold text-ink">Add it as its own organisation too</span>
                    Homes like this are usually run by a separate company that keeps its own records, so it can be sent
                    its own request. One entry, shown in both places.
                  </span>
                </label>
              )}
            </>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="secondary" onClick={savePlacement} disabled={!editing.type} data-testid="add-org-placement-save">
              Save this home
            </Button>
          </div>
        </div>
      )}
    </StepFrame>
  );
};

// ---- Who placed you there? ------------------------------------------------
const PlacedByStep = ({ headingRef, data, update, placingOrgs, isFosterHome }) => {
  const pb = data.placed_by || {};
  const setPb = (patch) => update({ placed_by: { ...pb, ...patch } });
  const where = isFosterHome ? 'there' : `at ${data.name || 'this home'}`;
  const optionClass = (on) =>
    `w-full text-left rounded-lg p-4 flex items-start gap-3 border-2 transition-colors duration-state ${
      on ? 'border-brand bg-surface' : 'border-transparent bg-ground hover:bg-inset'
    }`;
  const Radio = ({ on }) => (
    <span aria-hidden="true" className={`mt-1 w-[18px] h-[18px] rounded-full border-2 shrink-0 ${on ? 'border-brand' : 'border-shape'} flex items-center justify-center`}>
      {on && <span className="w-2 h-2 rounded-full bg-brand" />}
    </span>
  );

  return (
    <StepFrame
      headingRef={headingRef}
      title={`Which council or fostering agency placed you ${where}?`}
      sub={isFosterHome
        ? 'Foster carers do not hold your file \u2014 the council or agency that placed you does. If you do not know yet, that is normal.'
        : 'The home keeps its own records, and so does whoever placed you there. If you do not know yet, that is normal.'}
    >
      <div className="space-y-2 max-w-2xl" role="radiogroup" aria-label="Who placed you">
        {placingOrgs.map((o) => {
          const on = pb.mode === 'existing' && pb.org_id === o.id;
          return (
            <button key={o.id} type="button" role="radio" aria-checked={on} onClick={() => setPb({ mode: 'existing', org_id: o.id })} className={optionClass(on)} data-testid={`add-org-placed-by-${o.id}`}>
              <Radio on={on} />
              <span>
                <span className="block font-semibold text-ink">{o.name}</span>
                <span className="block text-sm text-ink-55">
                  {o.unresolved ? 'Not yet known' : getOrgType(o.type)?.label}{o.location ? ` \u00b7 ${o.location}` : ''}
                </span>
              </span>
            </button>
          );
        })}

        <button type="button" role="radio" aria-checked={pb.mode === 'new'} onClick={() => setPb({ mode: 'new' })} className={optionClass(pb.mode === 'new')} data-testid="add-org-placed-by-new">
          <Radio on={pb.mode === 'new'} />
          <span>
            <span className="block font-semibold text-ink">{placingOrgs.length ? 'A different one \u2014 add it now' : 'I know which \u2014 add it now'}</span>
            <span className="block text-sm text-ink-55">We match it to the right records team.</span>
          </span>
        </button>
        {pb.mode === 'new' && (
          <div className="rounded-lg bg-ground p-4 space-y-4 ml-0 sm:ml-8" data-testid="add-org-placed-by-new-form">
            <div className="flex gap-2">
              {[['council', 'Council'], ['fostering_agency', 'Fostering agency']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setPb({ new_type: v, new_name: '', new_canonical: null, new_contact: null })} aria-pressed={pb.new_type === v}
                  className={`rounded-full px-3.5 py-2 text-sm font-medium border ${pb.new_type === v ? 'bg-brand-wash text-brand-ink border-brand-wash' : 'bg-surface text-ink-70 border-rule'}`}>
                  {l}
                </button>
              ))}
            </div>
            <OrgAutocomplete
              value={pb.new_name || ''}
              type={pb.new_type || 'council'}
              onChange={(v) => setPb({ new_name: v, new_canonical: null, new_contact: null })}
              onPick={(picked) => setPb({ new_name: picked.name, new_canonical: picked.id, new_contact: { email: picked.privacy_email || '', address: picked.address || '', website: picked.website || '', matched_alias: picked.matched_alias || null } })}
              placeholder={pb.new_type === 'fostering_agency' ? 'For example, Foster Care Associates' : 'For example, Birmingham City Council'}
              testId="add-org-placed-by-name"
            />
            {pb.new_contact?.matched_alias && (
              <p className="text-sm text-brand-ink" data-testid="add-org-placed-by-matched-alias">
                &ldquo;{pb.new_contact.matched_alias}&rdquo; no longer handles this directly &mdash;{' '}
                {pb.new_name} does, including (very likely) records from before any handover.
                We will mention the old name in your Subject Access Request so they can trace them.
              </p>
            )}
          </div>
        )}

        <button type="button" role="radio" aria-checked={pb.mode === 'unknown'} onClick={() => setPb({ mode: 'unknown', org_id: null })} className={optionClass(pb.mode === 'unknown')} data-testid="add-org-placed-by-unknown">
          <Radio on={pb.mode === 'unknown'} />
          <span>
            <span className="block font-semibold text-ink">I don&apos;t know yet</span>
            <span className="block text-sm text-ink-55">
              We will file it under &ldquo;{HOLDING_ORG_NAME}&rdquo;{data.location ? ` for ${data.location}` : ''}. Your school or GP records almost always name
              the council, and we will ask you to confirm when one turns up.
            </span>
          </span>
        </button>
      </div>
    </StepFrame>
  );
};

/** One collapsible tile on the extras step. Top-level so React keeps the
 *  inputs inside mounted (and focused) as the parent re-renders on typing. */
const ExtrasSection = ({ id, title, hint, isOpen, onToggle, children }) => (
  <div className="rounded-lg bg-ground">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full text-left p-4 flex items-start justify-between gap-3"
      data-testid={`add-org-extras-${id}`}
    >
      <span>
        <span className="block text-[15px] font-semibold text-ink">{title}</span>
        <span className="block text-sm text-ink-55 mt-0.5">{hint}</span>
      </span>
      <ChevronLeft className={`w-4 h-4 mt-1 text-ink-55 shrink-0 transition-transform duration-state ${isOpen ? '-rotate-90' : 'rotate-180'}`} aria-hidden="true" />
    </button>
    {isOpen && <div className="px-4 pb-4 space-y-3">{children}</div>}
  </div>
);

// ---- Anything else? -------------------------------------------------------
const ExtrasStep = ({ headingRef, data, update }) => {
  const [openSection, setOpenSection] = useState(data.contact_from_match ? null : 'contact');
  const [editContact, setEditContact] = useState(!data.contact_from_match);
  const toggle = (k) => setOpenSection((cur) => (cur === k ? null : k));

  return (
    <StepFrame
      headingRef={headingRef}
      title="Anything else that might help?"
      sub="Everything here is optional. You can skip straight to checking and saving."
    >
      <div className="space-y-3 max-w-2xl">
        <ExtrasSection
          id="contact"
          isOpen={openSection === 'contact'}
          onToggle={() => toggle('contact')}
          title="Their contact details"
          hint={data.contact_from_match ? 'Found from our list. Only change them if they look wrong.' : 'Leave blank if you do not know — we can often find them.'}
        >
          {data.contact_from_match && !editContact ? (
            <div className="space-y-1 text-[15px]">
              {data.email && <p><span className="text-ink-55">Email </span><span className="text-ink">{data.email}</span></p>}
              {data.website && <p><span className="text-ink-55">Website </span><span className="text-ink">{data.website}</span></p>}
              {data.address && <p><span className="text-ink-55">Address </span><span className="text-ink">{data.address}</span></p>}
              <button type="button" className="text-sm font-semibold text-brand hover:text-brand-ink" onClick={() => setEditContact(true)}>
                These aren&apos;t right
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold" htmlFor="add-org-email">Email address</Label>
                <Input id="add-org-email" type="email" value={data.email} onChange={(e) => update({ email: e.target.value })} placeholder="data.protection@example.gov.uk" className="bg-surface border-surface" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold" htmlFor="add-org-website">Website</Label>
                <Input id="add-org-website" value={data.website} onChange={(e) => update({ website: e.target.value })} placeholder="example.gov.uk" className="bg-surface border-surface" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold" htmlFor="add-org-address">Postal address</Label>
                <Textarea id="add-org-address" value={data.address} onChange={(e) => update({ address: e.target.value })} rows={2} className="bg-surface border-surface" />
              </div>
            </>
          )}
        </ExtrasSection>
        <ExtrasSection id="refs" isOpen={openSection === 'refs'} onToggle={() => toggle('refs')} title="Reference numbers" hint="Case numbers, NHS numbers and similar, if you have them.">
          <Input value={data.reference_numbers} onChange={(e) => update({ reference_numbers: e.target.value })} placeholder="Reference numbers" className="bg-surface border-surface" aria-label="Reference numbers" data-testid="add-org-refs" />
        </ExtrasSection>
        <ExtrasSection id="notes" isOpen={openSection === 'notes'} onToggle={() => toggle('notes')} title="Anything else" hint="A note for yourself, or something for the request letter.">
          <Textarea value={data.notes} onChange={(e) => update({ notes: e.target.value })} rows={3} className="bg-surface border-surface" aria-label="Notes" data-testid="add-org-notes" />
        </ExtrasSection>
      </div>
    </StepFrame>
  );
};

// ---- Check and save -------------------------------------------------------
const ReviewStep = ({ headingRef, data, steps, goTo, singular, placingOrgs = [], isFosterHome = false, respitePool = [] }) => {
  const typeInfo = getOrgType(data.type);
  const when = data.dates_unknown
    ? 'Not known'
    : [formatMonthYear(data.start_date), data.end_date ? formatMonthYear(data.end_date) : (data.start_date ? 'still going' : '')].filter(Boolean).join(' – ') || 'Not given';
  const pb = data.placed_by || {};
  const placedByText = pb.mode === 'existing'
    ? (placingOrgs.find((o) => o.id === pb.org_id)?.name || 'One of your organisations')
    : pb.mode === 'new' ? `${pb.new_name || ''} (new)`
      : pb.mode === 'unknown' ? `Not known yet \u2014 filed under \u201c${HOLDING_ORG_NAME}\u201d` : 'Not answered';
  const rows = isFosterHome ? [
    ['What', 'Foster home', 'type'],
    ['Carers', (data.carer_names || []).join(', ') || 'None added', 'home'],
    ['Called', data.name || 'Not given', 'home'],
    ['Town', data.location || 'Not given', 'home'],
    ['When', when, 'when'],
    ['Short breaks', (data.respite || []).length
      ? data.respite.map((e) => {
        const c = respitePool.find((x) => x.id === e.carer_id);
        const pat = respitePatternLabel(e.pattern);
        return c ? (pat ? `${c.name} (${pat.toLowerCase()})` : c.name) : null;
      }).filter(Boolean).join(', ')
      : 'None', 'respite'],
    ['Placed by', placedByText, 'placed_by'],
  ] : [
    ['What', typeInfo?.label || '', 'type'],
    ['Name', data.name || '\u2014', 'name'],
    ['Town', data.location || 'Not given', 'name'],
    ['When', when, 'when'],
    ['People', data.professionals.length ? data.professionals.map((p) => `${p.name} (${roleLabel(data.type, p.role).toLowerCase()})`).join(', ') : 'None added', 'people'],
    ...(steps.includes('placements')
      ? [['Placements', data.placements.length ? `${data.placements.length} \u2014 ${data.placements.map((p) => p.name || (p.carer_names || []).join(', ') || 'unnamed').join('; ')}` : 'None added', 'placements']]
      : []),
    ...(steps.includes('placed_by') ? [['Placed by', placedByText, 'placed_by']] : []),
    ['Contact details', data.email || data.website || data.address
      ? (data.contact_from_match ? `Found for you: ${data.email || data.website || data.address}` : (data.email || data.website || data.address))
      : 'Not known yet', 'extras'],
  ];
  return (
    <StepFrame
      headingRef={headingRef}
      title="Here is what you told us"
      sub="Check it over. Anything you skipped can be added later from the organisation's page."
    >
      <div className="rounded-lg bg-ground px-5 max-w-2xl" data-testid="add-org-review">
        {rows.map(([k, v, target]) => (
          <div key={k} className="flex items-start justify-between gap-4 py-3.5 border-b border-rule last:border-b-0">
            <div className="min-w-0">
              <p className="text-sm text-ink-55">{k}</p>
              <p className="text-[16px] text-ink font-medium break-words">{v}</p>
            </div>
            <button type="button" onClick={() => goTo(target)} className="text-sm font-semibold text-brand hover:text-brand-ink shrink-0">
              Change
            </button>
          </div>
        ))}
      </div>
      <p className="text-sm text-ink-55 max-w-2xl">
        {isFosterHome && pb.mode === 'unknown'
          ? 'Nothing is sent to a placeholder council. When a record names the council, we will ask you to confirm it, and then you can send them a request.'
          : isFosterHome
            ? 'Next we will ask whether you want to send the council or agency a request for your records. You can say not yet.'
            : `Next we will ask whether you want to send ${data.name || `this ${singular}`} a request for your records. You can say not yet.`}
      </p>
    </StepFrame>
  );
};

export default AddOrganisationWizard;
